<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ResponseGroupedByAppOrAppGroupNotifications } from '../types/response';
import { computed, ref, watch } from 'vue';
import AppListItem from './components/AppListItem.ce.vue';
import AppInfoPanel from './components/AppInfoPanel.ce.vue';
import NotificationListItem from './components/NotificationListItem.vue';
import { formatDate } from '@/v1/utils/dateformatter';
import Popper from 'vue3-popper';
import LoadingFa from './components/LoadingFa.vue';

/* Popper Content Component
 *
 * This component renders the content of the popper for notifications.
 */


const { isSuccess, isError, isLoading, data, isFetching, refetch } = useQuery({
    queryKey: ['notificationsList'],
    queryFn: async () => {
        const response = await fetch('/notifications_backend/v2/notifications');
        if (response.status !== 200) {
            throw new Error('Failed to fetch notifications list');
        }
        return await response.json() as ResponseGroupedByAppOrAppGroupNotifications;
    },
    enabled: false
})

const selectedApp = ref<string | null>(null);
const showMobileDetail = ref(false);

const selectApp = (appId: string) => {
    selectedApp.value = appId;
    showMobileDetail.value = true;
}

const backToApps = () => {
    showMobileDetail.value = false;
}


const appList = computed(() => {
    if (!isSuccess.value) {
        return [];
    }
    return Object.values(data.value!.entries).map(entry => ({
        id: entry.appOrAppGroupId,
        name: entry.name,
        description: entry.description,
        icon: entry.icon,
        unread: entry.unread,
        lastNotificationAt: new Date(entry.lastNotificationAt)
    })).sort((a, b) => {
        return b.lastNotificationAt.getTime() - a.lastNotificationAt.getTime();
    })
})

watch(appList, async () => {
    if (selectedApp.value !== null)
        return;
    // auto select app if appid is part of the url
    if (window.location.pathname.match(/\/apps\/([a-z0-9]+)/)) {
        const appId = window.location.pathname.match(/\/apps\/([a-z0-9]+)/)![1];
        console.info(appId, data.value!.entries[appId])
        if (data.value!.entries[appId]) {
            return selectedApp.value = appId;
        } else {
            // check if app is part of an app group
            try {
                const response = await fetch(`/notifications_backend/v2/app-group-for-app/${appId}`);
                if (response.ok) {
                    const json = await response.json();
                    if (json.appGroupId && data.value!.entries[json.appGroupId]) {
                        return selectedApp.value = json.appGroupId;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }
    }
    // auto select first app if none is selected
    selectedApp.value = appList.value[0]?.id || null;
}, {
    immediate: false
})

const notificationList = computed(() => {
    if (!isSuccess.value || !selectedApp.value) {
        return [];
    }
    const internalList = [...(data.value!.entries[selectedApp.value]?.
        notifications || [])].sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime());
    return internalList
})

const selectedAppInfo = computed(() => {
    if (!selectedApp.value) {
        return null;
    }
    return appList.value.find(app => app.id === selectedApp.value) || null;
})

const hasAtLeastOneReadNotification = computed(() => {
    return notificationList.value.some(n => typeof n.readAt === 'string');
});

const hasAtLeastOneUnreadNotification = computed(() => {
    return notificationList.value.some(n => !n.readAt);
});

const groupedNotifications = computed(() => {
    if (!isSuccess.value || !selectedApp.value) {
        return [];
    }
    const list = notificationList.value;
    const groups: { title: string, notifications: typeof list }[] = [];

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear();
    };

    let currentGroup: { title: string, notifications: typeof list } | null = null;

    for (const notification of list) {
        const date = new Date(notification.createDate);
        let groupTitle = '';

        if (isSameDay(date, today)) {
            groupTitle = 'Heute';
        } else if (isSameDay(date, yesterday)) {
            groupTitle = 'Gestern';
        } else {
            groupTitle = formatDate(date.getTime());
        }

        if (!currentGroup || currentGroup.title !== groupTitle) {
            currentGroup = { title: groupTitle, notifications: [] };
            groups.push(currentGroup);
        }
        currentGroup.notifications.push(notification);
    }
    return groups;
});

const getLocalized = (map: Record<string, string>) => {
    return map['de'] || map['en'] || Object.values(map)[0] || '';
}

const emit = defineEmits<{
    (e: 'notificationsUpdated'): void
}>();

const toggleRead = async (notificationId: string, newUnreadState: boolean) => {
    try {
        const method = newUnreadState ? 'DELETE' : 'PUT';
        const response = await fetch(`/notifications_backend/v2/notifications/${notificationId}/readTimestamp`, {
            method: method
        });

        if (response.ok) {
            refetch();
            emit('notificationsUpdated');
        }
    } catch (e) {
        console.error(e);
    }
}

const clickNotification = async (notificationId: string, target?: string) => {
    try {
        // Mark as read when clicked
        const response = await fetch(`/notifications_backend/v2/notifications/${notificationId}/readTimestamp`, {
            method: 'PUT'
        });

        if (response.ok) {
            refetch();
            emit('notificationsUpdated');
        }
    } catch (e) {
        console.error(e);
    }

    // Navigate to target if provided
    if (target) {
        window.location.href = target;
    }
}

const markAllAsRead = async (closePopper?: () => void) => {
    if (!selectedApp.value) return;
    try {
        if (!hasAtLeastOneUnreadNotification.value) {
            return window.alert('Alle Benachrichtigungen sind bereits als gelesen markiert.');
        }
        const response = await fetch(`/notifications_backend/v2/notifications/set-read-at-for-app-or-app-group/${selectedApp.value}`, {
            method: 'POST'
        });

        if (response.ok) {
            refetch();
            emit('notificationsUpdated');
        }
    } catch (e) {
        console.error(e);
    }
    if (closePopper) closePopper();
}

const deleteNotification = async (notificationId: string) => {
    try {
        const response = await fetch(`/notifications_backend/v2/notifications/${notificationId}`, {
            method: 'DELETE'
        });

        if (response.ok) {
            refetch();
            emit('notificationsUpdated');
        }
    } catch (e) {
        console.error(e);
    }
}

const deleteAllReadNotifications = async (closePopper?: () => void) => {
    if (!selectedApp.value) return;
    try {
        if (!hasAtLeastOneReadNotification.value) {
            return window.alert('Markiere zuerst einige Benachrichtigungen als gelesen, um sie löschen zu können.');
        }
        const response = await fetch(`/notifications_backend/v2/notifications/read-for-app-or-app-group/${selectedApp.value}`, {
            method: 'DELETE'
        });

        if (response.ok) {
            refetch();
            emit('notificationsUpdated');
        }
    } catch (e) {
        console.error(e);
    }
    if (closePopper) closePopper();
}

defineExpose({
    fetchNotifications: refetch,
})

</script>

<template>
    <div
        class="notifications-app panel lan:flex lan:w-[calc(100vw-30px)] lan:ml-[7.5px] lan:bg-white lan:p-0 lan:max-w-[650px] lan:min-h-[300px] lan:h-[calc(100vh-100px)] lan:flex-col lan:sm:w-[600px] lan:sm:ml-0">
        <div
            class="lan:flex lan:flex-row lan:flex-wrap lan:justify-between lan:items-center lan:select-none lan:px-[15px] lan:pt-[15px]">
            <h4 class="lan:m-0 lan:flex lan:items-center lan:gap-2">
                Benachrichtigungen
                <LoadingFa v-if="isFetching || isLoading" />
            </h4>
            <a href="/benachrichtigungen.htm" class="lan:hover:no-underline">
                <div class="lan:flex lan:flex-row lan:items-center lan:select-none lan:gap-[5px]">
                    <i class="fa-regular fa-gear"></i>
                    Einstellungen
                </div>
            </a>
        </div>
        <div class="lan:flex-1 lan:flex lan:flex-col lan:mt-2.5 lan:min-h-0">
            <template v-if="isError">
                <div lan:py-[5px] lan:px-[15px]>Fehler beim Laden der Benachrichtigungen.</div>
            </template>
            <template v-else-if="isSuccess">
                <template v-if="Object.keys(data!.entries).length === 0">
                    <div class="lan:flex-1 lan:flex lan:flex-row lan:justify-center lan:items-center lan:h-full">
                        <span class="lan:select-none">Keine
                            neuen Benachrichtigungen.</span>
                    </div>
                </template>
                <template v-else>
                    <!-- row -->
                    <div class="lan:flex-1 lan:flex lan:flex-row lan:min-h-0">
                        <div class="lan:flex lan:flex-col lan:gap-2.5 lan:py-[5px] lan:px-[15px] lan:sm:pr-[7.5px] lan:w-full lan:sm:w-[200px] lan:overflow-y-scroll lan:overscroll-none"
                            :class="{ 'lan:hidden lan:sm:flex': showMobileDetail }">
                            <AppListItem v-for="app in appList" :key="app.id" :name="app.name" :count="app.unread"
                                :active="app.id === selectedApp" @click="selectApp(app.id)" :icon="app.icon" />
                        </div>
                        <template v-if="notificationList.length > 0">
                            <div class="lan:flex-1 lan:flex lan:flex-col lan:pb-[5px] lan:sm:pl-[7.5px] lan:translate-0 lan:overflow-y-scroll"
                                :class="{ 'lan:hidden lan:sm:flex': !showMobileDetail }">
                                <!-- app info panel -->
                                <AppInfoPanel
                                    v-if="selectedAppInfo"
                                    :name="selectedAppInfo.name"
                                    :description="selectedAppInfo.description"
                                    :icon="selectedAppInfo.icon"
                                    class="lan:mt-2"
                                />
                                <!-- toolbar app list -->
                                <div
                                    class="lan:px-2 lan:py-2 lan:mb-0 lan:flex lan:flex-row lan:items-center lan:gap-2 lan:bg-transparent lan:backdrop-blur-sm lan:sm:backdrop-blur-none lan:sticky lan:top-0 lan:left-0 lan:right-0 lan:z-50 lan:sm:mb-[-35px]">
                                    <div class="lan:sm:hidden lan:cursor-pointer lan:select-none" @click="backToApps">
                                        <i class="fa-regular fa-chevron-left"></i> <span
                                            class="lan:font-bold">Zurück</span>
                                    </div>
                                    <!-- spacer to simulate space-between and end-->
                                    <div class="lan:flex-1"></div>
                                    <Popper placement="bottom-end" offsetDistance="0">
                                        <button
                                            class="lan:w-6 lan:h-6 lan:hover:bg-black/5 lan:rounded-full lan:transition-colors lan:sm:backdrop-blur-sm lan:flex lan:items-center lan:justify-center lan:text-gray-500 lan:bg-transparent lan:outline-none lan:border-none lan:cursor-pointer">
                                            <i class="fa-solid fa-ellipsis-vertical lan:text-sm"></i>
                                        </button>
                                        <template #content="{ close }">
                                            <div
                                                class="lan:bg-white lan:rounded-md lan:shadow-lg lan:border lan:border-gray-200 lan:py-1 lan:min-w-[180px]">
                                                <button @click.prevent.stop="markAllAsRead(close)"
                                                    :class="{
                                                        'lan:w-full lan:text-left lan:px-4 lan:py-2 lan:text-sm lan:hover:bg-gray-100 lan:flex lan:items-center lan:gap-2 lan:border-none lan:outline-none lan:bg-transparent lan:cursor-pointer lan:select-none': true,
                                                        'lan:text-[#008bd2]': hasAtLeastOneUnreadNotification,
                                                        'lan:text-blue-300 lan:cursor-not-allowed': !hasAtLeastOneUnreadNotification
                                                    }">
                                                    <i class="fa-regular fa-envelope-open lan:w-4"></i>
                                                    Alle als gelesen markieren
                                                </button>
                                                <button @click.prevent.stop="deleteAllReadNotifications(close)"
                                                    :class="{
                                                        'lan:w-full lan:text-left lan:px-4 lan:py-2 lan:text-sm lan:hover:bg-red-50 lan:flex lan:items-center lan:gap-2 lan:border-none lan:outline-none lan:bg-transparent lan:cursor-pointer lan:select-none': true,
                                                        'lan:text-red-600': hasAtLeastOneReadNotification,
                                                        'lan:text-red-400 lan:cursor-not-allowed': !hasAtLeastOneReadNotification
                                                    }">
                                                    <i class="fa-regular fa-trash-can lan:w-4"></i>
                                                    Gelesene löschen
                                                </button>
                                            </div>
                                        </template>
                                    </Popper>
                                </div>
                                <!-- notification list -->
                                <template v-for="group in groupedNotifications" :key="group.title">
                                    <div class="lan:px-[15px] lan:flex lan:flex-col lan:gap-2.5 lan:mb-2.5">
                                        <div
                                            class="lan:text-xs lan:font-bold lan:text-gray-500 lan:mt-2 lan:mb-1 lan:uppercase lan:tracking-wider lan:text-center lan:select-none">
                                            {{ group.title }}
                                        </div>
                                        <NotificationListItem v-for="notification in group.notifications"
                                            :key="notification.id" :id="notification.id"
                                            :title="getLocalized(notification.title)"
                                            :message="getLocalized(notification.message)"
                                            :timestamp="new Date(notification.createDate).getTime()"
                                            :target="notification.target || undefined"
                                            :initialUnread="!notification.readAt" @toggleRead="toggleRead"
                                            @delete="deleteNotification" @click="clickNotification" />
                                    </div>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <div class="lan:flex-1 lan:flex lan:flex-col lan:h-full"
                                :class="{ 'lan:hidden lan:sm:flex': !showMobileDetail }">
                                <!-- app info panel -->
                                <AppInfoPanel
                                    v-if="selectedAppInfo"
                                    :name="selectedAppInfo.name"
                                    :description="selectedAppInfo.description"
                                    :icon="selectedAppInfo.icon"
                                    class="lan:mt-2"
                                />
                                <div class="lan:flex-1 lan:flex lan:flex-row lan:justify-center lan:items-center">
                                    <div class="lan:flex lan:flex-col lan:items-center lan:gap-4">
                                        <div class="lan:flex lan:flex-row lan:items-center lan:gap-2 lan:cursor-pointer lan:sm:hidden lan:self-start"
                                            @click="backToApps">
                                            <i class="fa-regular fa-chevron-left"></i> <span
                                                class="lan:font-bold">Zurück</span>
                                        </div>
                                        <span class="lan:select-none">Keine neuen Benachrichtigungen für diese App.</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>